/**
 * Batch #14.186 — Préférences de notification client.
 *
 * Le client choisit par événement (premier avis, relance, etc.) les canaux
 * sur lesquels il souhaite être notifié : email, push, SMS.
 *
 * Si le téléphone n'est pas renseigné dans son profil, la case SMS est désactivée
 * avec un message d'invitation à compléter ses coordonnées.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Mail, Smartphone, Bell, CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalAuthH as authH } from "@/api";


const CHANNEL_META = {
  email: { label: "Courriel", Icon: Mail, tone: "blue" },
  push:  { label: "Push", Icon: Bell, tone: "indigo" },
  sms:   { label: "SMS", Icon: Smartphone, tone: "emerald" },
};

const toneClasses = {
  blue: "border-blue-300 bg-blue-50 text-blue-700",
  indigo: "border-indigo-300 bg-indigo-50 text-indigo-700",
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

export default function NotificationPrefsTab({ primaryColor = "#072354" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/notifications/prefs`, { headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger vos préférences");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (evt, channel) => {
    setData((prev) => {
      const current = prev.prefs[evt] || [];
      const next = current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel];
      return { ...prev, prefs: { ...prev.prefs, [evt]: next } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/notifications/prefs`, { prefs: data.prefs }, { headers: authH() });
      toast.success("Préférences enregistrées");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) return null;

  const phoneOk = data.phone_configured;
  const channels = data.channels || ["email", "push", "sms"];

  return (
    <div className="max-w-2xl mx-auto" data-testid="portal-notification-prefs-tab">
      <div className="mb-5">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Bell className="w-5 h-5" style={{ color: primaryColor }} />
          Mes préférences de notification
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Choisissez comment vous souhaitez être averti pour chaque type d'événement.
        </p>
      </div>

      <div className={`rounded-xl p-3 mb-4 text-xs ${phoneOk ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
        {phoneOk ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>SMS disponible — votre numéro : <b>{data.phone}</b></span>
          </div>
        ) : (
          <div>
            <b>SMS désactivé.</b> Ajoutez votre numéro de téléphone dans la tuile <b>Info</b> pour activer les notifications par texto.
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="portal-notif-matrix">
        {(data.catalog || []).map((evt) => (
          <div key={evt.key} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`portal-notif-row-${evt.key}`}>
            <div className="mb-2">
              <div className="font-semibold text-sm text-slate-800">{evt.label}</div>
              <div className="text-[11px] text-slate-500">{evt.desc}</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {channels.map((ch) => {
                const meta = CHANNEL_META[ch];
                if (!meta) return null;
                const enabled = (data.prefs[evt.key] || []).includes(ch);
                const disabled = ch === "sms" && !phoneOk;
                return (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => !disabled && toggle(evt.key, ch)}
                    disabled={disabled}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition ${
                      enabled
                        ? `${toneClasses[meta.tone]} shadow-sm`
                        : disabled
                          ? "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                    data-testid={`portal-notif-toggle-${evt.key}-${ch}`}
                    title={`${meta.label}${disabled ? " — Téléphone manquant" : ""}`}
                  >
                    <meta.Icon className="w-3 h-3" />
                    {meta.label}
                    {enabled && <CheckCircle2 className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-5">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
          data-testid="portal-notification-prefs-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
